import React, { useEffect, useState } from "react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?w=1920&q=85&fm=jpg&fit=crop";

const AnimatedLine = ({ text, delayStart = 0 }) => {
  const chars = text.split("");
  return (
    <span className="block leading-[0.9]">
      {chars.map((c, i) => (
        <span
          key={i}
          className="letter"
          style={{ animationDelay: `${delayStart + i * 45}ms` }}
        >
          {c === " " ? " " : c}
        </span>
      ))}
    </span>
  );
};

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full bg-black flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background image */}
      <div
        className="hero-bg-image"
        style={{
          backgroundImage: `url("${HERO_IMAGE}")`,
          transform: `translateY(${scrollY * 0.38}px)`,
        }}
        aria-hidden="true"
      />

      {/* Dark gradient overlays */}
      <div className="hero-overlay" aria-hidden="true" />
      <div className="hero-overlay-left" aria-hidden="true" />

      {/* Film grain */}
      <div className="hero-grain" aria-hidden="true" />

      {/* Red glow */}
      <div className="hero-glow" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-20 max-w-[1500px] mx-auto w-full px-6 md:px-12 lg:px-20 pt-32 pb-20">
        <div className="flex items-center gap-4 mb-8">
          <span className="divider-red" />
          <span className="font-heading text-[#c0392b] tracking-[0.4em] text-xs uppercase" data-testid="hero-eyebrow">
            01 — Fotografia em Movimento
          </span>
        </div>

        <h1
          className="font-heading text-white uppercase font-normal tracking-[-0.02em]"
          data-testid="hero-headline"
        >
          <span className="block text-[26vw] md:text-[22vw] lg:text-[18rem] leading-[0.85]">
            <AnimatedLine text="VER." delayStart={0} />
          </span>
          <span className="block text-[10vw] md:text-[8.5vw] lg:text-[8rem] leading-[0.9] mt-2">
            <AnimatedLine text="SENTIR. CAPTURAR." delayStart={300} />
          </span>
        </h1>

        <div className="mt-10 max-w-2xl">
          <p className="text-white/80 text-base md:text-lg leading-relaxed" data-testid="hero-subtitle">
            Fotografia desportiva e artística — Portugal.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <button
            onClick={() => scrollTo("portfolio")}
            className="cta-primary magnetic-btn"
            data-testid="hero-cta-portfolio"
          >
            Ver Portfólio
          </button>
          <button
            onClick={() => scrollTo("contacto")}
            className="cta-secondary magnetic-btn"
            data-testid="hero-cta-contact"
          >
            Contactar
          </button>
        </div>

        {/* Scroll hint right */}
        <div className="absolute bottom-10 right-6 md:right-12 lg:right-20 flex items-center gap-3 text-white/40 text-xs tracking-[0.3em] uppercase font-heading">
          <span>Scroll</span>
          <span className="block w-10 h-px bg-[#c0392b]" />
        </div>

        {/* Animated scroll line left */}
        <div className="absolute bottom-10 left-6 md:left-12 lg:left-20 flex flex-col items-center gap-2" aria-hidden="true">
          <div className="w-px h-14 bg-white/15 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full bg-[#c0392b] scroll-line" style={{ height: "40%" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
